import React, { useContext, useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { Button, ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUserSlash } from '@fortawesome/pro-regular-svg-icons';
import { Overlay } from 'src/components/Overlay';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAuthorProfileData,
  fetchOrganizationAndJournalsInformation
} from 'src/api/queries';
import AuthorInfo from '../AuthorInfo';
import SummaryTab from '../AuthorSummary';
import PublicationHistoryTab from './PublicationHistoryTab';
import ReviewListTab from './ReviewTab';
import ManageAuthorTags from './ManageAuthorTag';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import { IAuthorWithEmail, IRoleType } from '@publishing-engine/domain/models';
import { useWorkspace } from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import UserComposeEmailModal from '../UserComposeEmailModal';
import { AdHocModalOpen } from '../ComposeEmailModal';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import {
  getJournalReviewer,
  getReviewerJournals
} from 'src/utils/getJournalReviewer';
import EmailLog from './EmailLog';
import { useKeycloak } from 'src/components/AuthHOC';
import NavbarDashboard from '../../../NavbarDashboard';
import { SubmissionDataContext } from 'src/contexts/SubmissionContextProvider';
import { IUser } from '@publishing-engine/domain/fixtures';
import SingleItem from 'src/layouts/SingleItem';
import { SubmissionDataContext as SubmissionDataProviderContext } from 'src/contexts/SubmissionDataProvider';
import {
  fetchUserJournalSpecificKeywords,
  IUserJournalSpecificKeywordGroup
} from 'src/api/signUpMutation';
import AuditLog from './AuditLog';
interface Props {
  onBackClick: () => void;
  userId: string;
  backButtonText?: string;
  author?: IAuthorWithEmail | ISubmissionAuthor;
  roles?: Array<string>;
  isProxyUserNotRequired?: boolean;
  showReviewerActivity?: boolean;
  showReviewerActivityForEachJournal?: boolean;
  roleContext?: IRoleType;
  reviewId?: string;
  submissionId?: string;
  user?: IUser;
}

const TABS = {
  SUMMARY: 'Summary',
  PUBLICATION_HISTORY: 'Publication History',
  REVIEWS: 'Reviews',
  EMAIL_LOG: 'Email Log',
  AUDIT_LOG: 'Activity'
};

type Tab = (typeof TABS)[keyof typeof TABS];
export type JournalList = { id: string; name: string; shortname: string }[];

const SubmittedAuthorDetailsModal: React.FC<Props> = props => {
  const {
    userId,
    onBackClick,
    backButtonText,
    author,
    showReviewerActivity,
    showReviewerActivityForEachJournal = false,
    roleContext,
    user
  } = props;
  const [activeTab, setActiveTab] = useState<Tab>(TABS.SUMMARY);
  const [effectiveUserId, setEffectiveUserId] = useState(userId);
  const isViewingMergedAccount = effectiveUserId !== userId;
  const { workspaceId, orgId } = useWorkspace();
  const submissionContextId = useCurrentContextId() ?? '';
  const submissionContextData = useContext(SubmissionDataContext);
  const submissionData = useContext(SubmissionDataProviderContext);
  const reviewerConfig =
    submissionContextData?.submissionContext?.config?.cms?.lexicon?.fields;
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });

  const { data: authorData, isLoading } = useQuery({
    queryKey: ['workspace-author-profile', { orgId, userId: effectiveUserId }],
    queryFn: () => fetchAuthorProfileData(orgId, effectiveUserId)
  });
  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () =>
      orgId ? fetchOrganizationAndJournalsInformation(orgId) : null
  });

  const { data: currentUserData } = useQuery({
    queryKey: ['user-journal-specific-keywords', { userId: effectiveUserId }],
    queryFn: () => fetchUserJournalSpecificKeywords(effectiveUserId)
  });

  const currentUserJournals: JournalList =
    currentUserData?.data?.journalSpecificKeywords?.map(
      (journal: IUserJournalSpecificKeywordGroup) => ({
        id: journal.submissionContextId,
        name: journal.journalName,
        shortname: journal.journalShortname
      })
    ) ?? [];

  const { journalName, isReviewer } = getJournalReviewer(
    authorData,
    submissionContextId
  );

  const { journals: reviewerJournals, isReviewer: isUserReviewer } =
    getReviewerJournals(authorData);

  const isProxyUser = authorData?.isProxyUser;

  const isNotActiveUser = authorData?.user?.status
    ? authorData.user.status !== 'active'
    : false;

  const { currentUser, keycloak } = useKeycloak();
  const isLoggedInUser = currentUser?.id === effectiveUserId;

  const isProxySession = keycloak?.tokenParsed?.IMPERSONATOR_USERNAME;

  const mergedAccount = authorData?.mergedAccount;
  const isDeactivatedAccount =
    Boolean(mergedAccount) && !isViewingMergedAccount;

  const profileUser =
    ((!authorData?.user?.firstName || !authorData?.user?.lastName) && author
      ? author
      : authorData?.user) || user;

  const visibleTabs = Object.values(TABS).filter(tab => {
    if (tab === TABS.EMAIL_LOG && !submissionData && !props.submissionId) {
      return false;
    }
    return true;
  });

  const loadingPanel = (
    <Flex justifyContent="center" padding={10}>
      <Spinner />
    </Flex>
  );

  const renderTab = (activeTab: Tab) => {
    switch (activeTab) {
      case TABS.SUMMARY:
        return (
          <Flex direction="column" gap={5}>
            <SummaryTab
              data={{
                affiliationDetails: authorData?.user?.affiliationDetails,
                additionalAffiliations:
                  authorData?.user?.additionalAffiliations,
                boardMemberships: authorData?.boardMemberships,
                userId: userId,
                journalName: journalName,
                reviewerJournals: reviewerJournals
              }}
              showReviewerActivity={showReviewerActivity && isReviewer}
              showReviewerActivityForEachJournal={
                showReviewerActivityForEachJournal && isUserReviewer
              }
              reviewerConfig={reviewerConfig}
              journals={currentUserJournals}
              submissionContextId={submissionContextId}
              organizationId={orgId}
              user={authorData?.user}
              profileUser={profileUser}
              isDeactivatedAccount={isDeactivatedAccount}
            />
          </Flex>
        );
      case TABS.PUBLICATION_HISTORY:
        if (!organizationData) return loadingPanel;
        return (
          <PublicationHistoryTab
            workspaceId={workspaceId}
            userId={userId}
            organizationData={organizationData!}
          />
        );
      case TABS.REVIEWS:
        if (!organizationData) return loadingPanel;
        return (
          <ReviewListTab
            workspaceId={workspaceId}
            userId={userId}
            organizationData={organizationData!}
          />
        );
      case TABS.EMAIL_LOG:
        return (
          <EmailLog
            user={
              (!authorData?.user?.firstName || !authorData?.user?.lastName) &&
              author
                ? {
                    email: author.email ?? '',
                    firstName: author.firstName,
                    lastName: author.lastName
                  }
                : authorData?.user
                ? {
                    email: authorData.user.email ?? '',
                    firstName: authorData.user.firstName ?? '',
                    lastName: authorData.user.lastName ?? ''
                  }
                : undefined
            }
            roleContext={roleContext}
            submissionId={props.submissionId}
          />
        );
      case TABS.AUDIT_LOG:
        return <AuditLog userId={userId} />;
      default:
        return null;
    }
  };

  return (
    <>
      {openSendEmailModal.status && (
        <UserComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          organizationId={orgId}
          submissionContextId={submissionContextId}
          email={openSendEmailModal.defaultEmail}
        />
      )}
      <Overlay>
        <Flex
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
          minH="100vh"
        >
          <NavbarDashboard />
          <Box
            paddingTop={4}
            paddingX={{ base: 3, md: 6, xl: 8 }}
            paddingBottom={8}
          >
            <Flex
              gap={5}
              flexDirection="column"
              justifyContent="start"
              width="100%"
              maxWidth="1180px"
              marginX="auto"
            >
              <ButtonText
                leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                maxWidth="fit-content"
                onClick={onBackClick}
              >
                {backButtonText || 'Back to Authors'}
              </ButtonText>

              {/* Deactivated account card */}
              {isDeactivatedAccount && !isViewingMergedAccount ? (
                <Box
                  backgroundColor="surfacePrimaryFill"
                  borderWidth="1px"
                  borderColor="neutralPrimaryBorder"
                  borderRadius="8px"
                  overflow="hidden"
                  boxShadow="objectDefault"
                >
                  <Box height="4px" backgroundColor="actionPrimaryFill" />
                  {isLoading ? (
                    <Flex justifyContent="center" padding={10}>
                      <Spinner />
                    </Flex>
                  ) : (
                    <Flex
                      gap={6}
                      justifyContent="space-between"
                      flexDirection={{ base: 'column', lg: 'row' }}
                      padding={{ base: 4, md: 6 }}
                      alignItems={{ base: 'stretch', lg: 'flex-start' }}
                    >
                      <Flex direction="column" gap={4} flex="1" minWidth={0}>
                        {(authorData || author || user) && (
                          <AuthorInfo
                            user={profileUser}
                            isLoading={isLoading}
                            additionalInformation={
                              authorData?.additionalInformation
                            }
                            journals={authorData?.journals}
                            isDeactivatedAccount={isDeactivatedAccount}
                            isSubmittedAuthor={true}
                            affiliationDetails={
                              authorData?.user?.affiliationDetails
                            }
                          />
                        )}
                        <Flex
                          gap={3}
                          alignItems="center"
                          padding={3}
                          backgroundColor="attentionSecondaryFill"
                          borderRadius="4px"
                          borderWidth="1px"
                          borderColor="attentionPrimaryBorder"
                        >
                          <FontAwesomeIcon
                            icon={faUserSlash}
                            color="var(--chakra-colors-attentionPrimaryFill)"
                          />
                          <Flex direction="column">
                            <Text textStyle="headingItem">
                              Account Deactivated
                            </Text>
                            <Text textStyle="textBody">
                              This account has been deactivated and merged into
                              another account.
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                      <Flex
                        gap={3}
                        flexShrink={0}
                        alignSelf={{ base: 'flex-start', lg: 'flex-start' }}
                        flexWrap="wrap"
                      >
                        <Button
                          width="fit-content"
                          variant="primary"
                          onClick={() => setEffectiveUserId(mergedAccount!.id)}
                        >
                          View Merged Account
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </Box>
              ) : (
                <>
                  <Box
                    backgroundColor="surfacePrimaryFill"
                    borderWidth="1px"
                    borderColor="neutralPrimaryBorder"
                    borderRadius="8px"
                    overflow="hidden"
                    boxShadow="objectDefault"
                  >
                    <Box height="4px" backgroundColor="actionPrimaryFill" />
                    {isLoading ? (
                      <Flex justifyContent="center" padding={10}>
                        <Spinner />
                      </Flex>
                    ) : (
                      <Flex
                        gap={6}
                        justifyContent="space-between"
                        flexDirection={{ base: 'column', lg: 'row' }}
                        padding={{ base: 4, md: 6 }}
                        alignItems={{ base: 'stretch', lg: 'flex-start' }}
                      >
                        {/* Author section */}
                        {(authorData || author || user) && (
                          <Flex
                            gap={4}
                            alignItems="flex-start"
                            minWidth={0}
                            flex="1"
                          >
                            <Flex
                              flexDirection="column"
                              gap={3}
                              minWidth={0}
                              flex="1"
                            >
                              <Flex direction="column" gap={3}>
                                {isViewingMergedAccount && (
                                  <ButtonText
                                    maxWidth="fit-content"
                                    variant="secondary"
                                    leftIcon={
                                      <FontAwesomeIcon icon={faArrowLeft} />
                                    }
                                    onClick={() => setEffectiveUserId(userId)}
                                  >
                                    Back to original account
                                  </ButtonText>
                                )}
                                <Flex width="100%" alignItems="flex-start">
                                  <AuthorInfo
                                    user={profileUser}
                                    isLoading={isLoading}
                                    additionalInformation={
                                      authorData?.additionalInformation
                                    }
                                    journals={authorData?.journals}
                                    isSubmittedAuthor={true}
                                    affiliationDetails={
                                      authorData?.user?.affiliationDetails
                                    }
                                    showActions={true}
                                    onSendEmail={email =>
                                      setOpenSendEmailModal({
                                        status: true,
                                        defaultEmail: [email]
                                      })
                                    }
                                    isProxyUser={isProxyUser}
                                    isLoggedInUser={isLoggedInUser}
                                    isProxySession={Boolean(isProxySession)}
                                    userId={
                                      author?.id || authorData?.userId || ''
                                    }
                                    reviewId={props.reviewId}
                                    proxyRole={
                                      props.reviewId
                                        ? 'reviewer'
                                        : props.submissionId
                                        ? 'author'
                                        : undefined
                                    }
                                    proxyAuthorData={authorData ?? null}
                                  />
                                </Flex>
                                <Box
                                  borderTopWidth="1px"
                                  borderTopColor="neutralPrimaryBorder"
                                  paddingTop={3}
                                >
                                  <ManageAuthorTags
                                    allTags={authorData?.tags || []}
                                    orgId={orgId}
                                    userId={effectiveUserId}
                                    workspaceId={workspaceId}
                                    disableTagAction={isNotActiveUser}
                                  />
                                </Box>
                              </Flex>
                            </Flex>
                          </Flex>
                        )}
                      </Flex>
                    )}
                  </Box>

                  {/* Author tabs */}
                  <Box width="100%" backgroundColor="surfacePrimaryFill">
                    <SingleItem.Navigation size="compact">
                      {visibleTabs.map(tab => (
                        <SingleItem.Tab
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          isActive={tab === activeTab}
                        >
                          {tab}
                        </SingleItem.Tab>
                      ))}
                    </SingleItem.Navigation>
                    <Box
                      width="100%"
                      backgroundColor="surfacePrimaryFill"
                      padding={{ base: 4, md: 6 }}
                    >
                      {renderTab(activeTab)}
                    </Box>
                  </Box>
                </>
              )}
            </Flex>
          </Box>
        </Flex>
      </Overlay>
    </>
  );
};

export default SubmittedAuthorDetailsModal;
